import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Head-coverage check: is the whole head inside the frame?
 *
 * Cropping is the most common reason a photo silently becomes unusable, and it
 * also invalidates the head-size measurement — so this check runs before the
 * head-size verdict is trusted by the user.
 */
export const headCoverageCheck: ValidationCheck = {
  id: 'head_coverage',
  title: 'Голова целиком в кадре',
  description: 'Проверяет, что кадр не обрезает макушку, подбородок и бока головы.',
  kind: 'geometry',
  requires: ['geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['geometry']);
    if (missing) return notChecked(ctx, headCoverageCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    if (!geometry) return notChecked(ctx, headCoverageCheck, 'Геометрия недоступна.', ctx.rule.severity);

    const minMargin = numberParam(ctx.rule.params, 'minMargin', 0.004);
    const margins = geometry.margins;
    const cropped = {
      top: margins.top <= minMargin,
      bottom: margins.bottom <= minMargin,
      left: margins.left <= minMargin,
      right: margins.right <= minMargin,
    };
    const croppedSides = Object.entries(cropped)
      .filter(([, value]) => value)
      .map(([side]) => side);
    const anyCropped = croppedSides.length > 0;

    const sideLabel: Record<string, string> = {
      top: 'макушка',
      bottom: 'подбородок',
      left: 'левая сторона головы',
      right: 'правая сторона головы',
    };

    const problemRegions: NonNullable<CheckResult['problemRegions']> = [];
    if (cropped.top) {
      problemRegions.push({
        id: 'head_cropped_top',
        rect: { x: 0, y: 0, width: 1, height: 0.06 },
        message: 'Кадр обрезает верх головы.',
        severity: 'error',
      });
    }
    if (cropped.bottom) {
      problemRegions.push({
        id: 'head_cropped_bottom',
        rect: { x: 0, y: 0.94, width: 1, height: 0.06 },
        message: 'Кадр обрезает подбородок.',
        severity: 'error',
      });
    }

    return plainResult({
      id: headCoverageCheck.id,
      title: headCoverageCheck.title,
      status: anyCropped ? 'fail' : 'pass',
      severity: ctx.rule.severity,
      value: Math.min(margins.top, margins.bottom, margins.left, margins.right),
      message: anyCropped
        ? `Кадр обрезает: ${croppedSides.map((side) => sideLabel[side] ?? side).join(', ')}.`
        : 'Голова полностью помещается в кадре, со всех сторон есть небольшой запас.',
      requirement: 'Голова должна быть видна целиком: от макушки с волосами до подбородка.',
      fixability: 'retake',
      geometry: [
        { type: 'rect', coordinates: [0, 0, 1, 1] },
        {
          type: 'rect',
          coordinates: [
            roundTo(geometry.headBox.x, 4),
            roundTo(geometry.headBox.y, 4),
            roundTo(geometry.headBox.width, 4),
            roundTo(geometry.headBox.height, 4),
          ],
        },
      ],
      problemRegions,
      confidence: roundTo(geometry.headTopConfidence.value, 2),
      details: { margins, cropped, displayValue: anyCropped ? 'обрезана' : 'целиком' },
    });
  },
};
